import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import { Loading, LoadingOverlay } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import NoResults from "@/components/NoResults/NoResults";
import clsx from "clsx";
import * as Sentry from "@sentry/react";
import { deducedError } from "@/Utils/ErrorUtils";
import {
  timeOfDaySchema,
  type IActivityCategory,
  type IActivityOccurrenceOrGroup,
  type ITimeOfDay,
  orderedCategories,
} from "@medoma/models/activities";
import { useUnplannedOccurrencesAndGroups } from "@/api/Activities";
import {
  useTeams,
  usePatientInformationFields,
  informationFieldConfigValueSchema,
} from "@/api/Patients";
import { useEmployees } from "@/api/Employee";
import styles from "./PlanningSideBar.module.scss";
import { MedomaGridList } from "@/components/DragAndDrop/DragAndDrop";
import { useLayoutEffect, useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getGroupedRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { getDataFromActivityOccurrencesAndGroups } from "../../Activities/ActivitiesTable/getDataFromActivityOccurrences";
import { matchesPatientOrActivityTitleSearch } from "../../Activities/ActivitiesTable/activitiesTableUtils";
import { ControlledSelect } from "@medoma/components/Select/Select";
import {
  GroupedRows,
  type IActivityTableItem,
  type IGrouping,
} from "@/pages/commandcenter/Planning/SideBar/GroupedRows";
import {
  type IMedicalCompetence,
  medicalCompetenceDictionary,
  employeeName,
  type IEmployee,
} from "@medoma/models/shifts";
import { timeOfDay } from "@/api/Routes";
import { useLingui } from "@lingui/react/macro";
import { useSelection } from "../Selection/SelectionContext";
import { useSelectionInSidebar } from "../Selection/useSelectionInSidebar";
import { usePatientOrActivityTitleFilter } from "../PlanningContext";
import { useIsSidebarDropZoneAvailable } from "../DragAndDrop/useIsSidebarDropZoneAvailable";
import { useDropToSidebar } from "../DragAndDrop/useDropToSidebar";
import { useDragAndDropFromSidebar } from "../DragAndDrop/useDragAndDropFromSidebar";
import {
  compareTimespans,
  getTitleFromActivityOccurrenceOrGroup,
} from "./planningSideBarSortingUtils";
import { useMap } from "react-map-gl/mapbox";
import {
  getPatientFullName,
  patientStatusSchema,
  type ITeam,
} from "@medoma/models/patients";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { Text } from "@medoma/components/Text/Text";
import { useTimeFilter } from "../useTimeFilter";
import { useCompetenceFilter } from "../useCompetenceFilter";
import { useTeamFilter } from "../useTeamFilter";
import { isAnyTimeOfDayVisible } from "@/api/timeWindow";
import { useLocalStorage } from "@medoma/shared-employee/useLocalStorage";
import { useUserProfile } from "@/api/Users";
import { DropZone } from "@/components/DragAndDrop/DropZone";

const columnHelper = createColumnHelper<IActivityTableItem>();

const GROUPABLE_COLUMNS = [
  "category",
  "patient",
  "requiredCompetences",
  "timespan",
  "areaDisplayName",
  "team",
  "primaryResponsible",
] as const satisfies readonly IGrouping["id"][];

const COLUMN_DISPLAY_NAMES = {
  category: msg`Aktivitetstyp`,
  patient: msg`Patient`,
  requiredCompetences: msg`Kompetens`,
  timespan: msg`Tid`,
  areaDisplayName: msg`Område`,
  team: msg`Team`,
  primaryResponsible: msg`Patientansvarig`,
};

const NO_COMPETENCE = msg`Inget kompetensbehov`;
const SEVERAL_COMPETENCES = msg`Flera kompetenser`;

const DEFAULT_SORTING = [
  { id: "timespan", desc: false },
  { id: "title", desc: false },
];

const columns = (
  translator: ReturnType<typeof useLingui>["t"],
  employeesMap: Map<number, IEmployee>,
) => [
  columnHelper.accessor("category", {
    header: translator(COLUMN_DISPLAY_NAMES["category"]),
    cell: ({ getValue }) => {
      return getValue();
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<IActivityCategory>("category");
      const bState = b.getValue<IActivityCategory>("category");

      // Sort by orderedCategories
      if (
        orderedCategories.indexOf(aState) < orderedCategories.indexOf(bState)
      ) {
        return -1;
      } else if (
        orderedCategories.indexOf(aState) > orderedCategories.indexOf(bState)
      ) {
        return 1;
      } else {
        return 0;
      }
    },
  }),
  columnHelper.accessor("timespan", {
    header: translator(COLUMN_DISPLAY_NAMES["timespan"]),
    getGroupingValue: ({ timespan }) => {
      const [start, _, type] = timespan;
      return timeOfDay(
        type === timeOfDaySchema.enum.Any ? timeOfDaySchema.enum.Any : start,
      );
    },
    sortingFn: (a, b) =>
      compareTimespans(
        a.getValue<[Date, Date, ITimeOfDay]>("timespan"),
        b.getValue<[Date, Date, ITimeOfDay]>("timespan"),
      ),
    filterFn: (row, columnId, filterValue) => {
      const [rowStart, rowEnd, timeOfDay] =
        row.getValue<[Date, Date, ITimeOfDay]>(columnId);

      // Handle scenario where no filter is applied
      if (!filterValue) {
        return true;
      }

      if (timeOfDay === timeOfDaySchema.enum.Any) {
        return isAnyTimeOfDayVisible(filterValue);
      }

      const { startTime, endTime } = filterValue;

      if (rowEnd < startTime) {
        return false;
      }
      if (rowStart > endTime) {
        return false;
      }

      return true;
    },
  }),
  columnHelper.accessor("requiredCompetences", {
    header: translator(COLUMN_DISPLAY_NAMES["requiredCompetences"]),
    getGroupingValue: ({ requiredCompetences }) => {
      if (!requiredCompetences || !requiredCompetences[0]) {
        return translator(NO_COMPETENCE);
      }
      if (requiredCompetences.length > 1) {
        return translator(SEVERAL_COMPETENCES);
      }
      return translator(
        medicalCompetenceDictionary[requiredCompetences[0]].short,
      );
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<IMedicalCompetence[] | undefined>(
        "requiredCompetences",
      );
      const bState = b.getValue<IMedicalCompetence[] | undefined>(
        "requiredCompetences",
      );

      // Sort competence-less activities to the top
      if (!aState && bState) {
        return -1;
      }
      if (aState && !bState) {
        return 1;
      }

      if (aState && bState) {
        // Sort activities with several competences to the bottom
        if (aState.length > 1 && bState.length <= 1) {
          return 1;
        }
        if (aState.length <= 1 && bState.length > 1) {
          return -1;
        }
      }

      return 0;
    },
    filterFn: (row, columnId, filterValue) => {
      const rowValue = row.getValue<IMedicalCompetence[] | undefined>(columnId);

      if (!(filterValue instanceof Set)) {
        return true;
      }

      // Activities with no competence requirement always show
      if (!rowValue || rowValue.length === 0) {
        return true;
      }

      return rowValue.some((competence) => filterValue.has(competence));
    },
  }),
  columnHelper.accessor("patient", {
    header: translator(COLUMN_DISPLAY_NAMES["patient"]),
    getGroupingValue: ({ patient }) => {
      return patient ? patient.id : undefined;
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<
        IActivityOccurrenceOrGroup["patient"] | undefined
      >("patient");
      const bState = b.getValue<
        IActivityOccurrenceOrGroup["patient"] | undefined
      >("patient");

      // Sort patient-less activities to the top
      if (!aState && bState) {
        return -1;
      }
      if (aState && !bState) {
        return 1;
      }

      if (aState && bState) {
        // Sort activities with deleted patients to the bottom
        const aDeleted = aState.status === patientStatusSchema.enum.deleted;
        const bDeleted = bState.status === patientStatusSchema.enum.deleted;
        if (aDeleted && !bDeleted) {
          return 1;
        }
        if (!aDeleted && bDeleted) {
          return -1;
        }

        if (!aDeleted && !bDeleted) {
          const aName = getPatientFullName(aState.name);
          const bName = getPatientFullName(bState.name);
          if (aName < bName) {
            return -1;
          }
          if (aName > bName) {
            return 1;
          }
        }
      }

      return 0;
    },
    // Patient or activity title search from PageToolbar: same filterFn as in columnFilters (one column only).
    filterFn: (row, _columnId, filterValue) =>
      matchesPatientOrActivityTitleSearch(
        typeof filterValue === "string" ? filterValue : "",
        row.original.patient,
        row.original.activityOccurrenceOrGroup,
      ),
  }),
  columnHelper.accessor("areaDisplayName", {
    header: translator(COLUMN_DISPLAY_NAMES["areaDisplayName"]),
    getGroupingValue: ({ areaDisplayName }) => {
      if (!areaDisplayName) {
        return "-";
      }
      return areaDisplayName;
    },
    sortingFn: "text",
    sortUndefined: "last",
  }),
  columnHelper.accessor("team", {
    header: translator(COLUMN_DISPLAY_NAMES["team"]),
    getGroupingValue: ({ team }) => {
      if (!team?.displayName) {
        return "-";
      }
      return team.displayName;
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<typeof a.original.team>("team");
      const bState = b.getValue<typeof b.original.team>("team");

      // Sort team-less activities to the top
      if (!aState && bState) {
        return -1;
      }
      if (aState && !bState) {
        return 1;
      }

      if (aState && bState) {
        if (aState.displayName < bState.displayName) {
          return -1;
        }
        if (aState.displayName > bState.displayName) {
          return 1;
        }
      }

      return 0;
    },
    filterFn: (row, columnId, filterValue) => {
      const rowValue = row.getValue<ITeam | undefined>(columnId);

      if (!(filterValue instanceof Set) || filterValue.size === 0) {
        return true;
      }

      // Activities without a team always show
      if (!rowValue) {
        return true;
      }

      return filterValue.has(rowValue.id);
    },
  }),
  columnHelper.accessor("primaryResponsible", {
    header: translator(COLUMN_DISPLAY_NAMES["primaryResponsible"]),
    getGroupingValue: ({ primaryResponsible }) => {
      if (!primaryResponsible) {
        return "-";
      }
      const employee = employeesMap.get(primaryResponsible);
      if (!employee) {
        return "-";
      }
      return employeeName(employee, { employeeNameFormat: "full" });
    },
    sortingFn: (a, b) => {
      const aState = a.getValue<number | undefined>("primaryResponsible");
      const bState = b.getValue<number | undefined>("primaryResponsible");

      // Sort activities without primary responsible to the top
      if (!aState && bState) {
        return -1;
      }
      if (aState && !bState) {
        return 1;
      }

      if (aState && bState) {
        const aEmployee = employeesMap.get(aState);
        const bEmployee = employeesMap.get(bState);

        if (!aEmployee && bEmployee) {
          return -1;
        }
        if (aEmployee && !bEmployee) {
          return 1;
        }

        if (aEmployee && bEmployee) {
          const aName = employeeName(aEmployee, { employeeNameFormat: "full" });
          const bName = employeeName(bEmployee, { employeeNameFormat: "full" });
          return aName.localeCompare(bName);
        }
      }

      return 0;
    },
  }),
  columnHelper.accessor(
    (row) =>
      getTitleFromActivityOccurrenceOrGroup(row.activityOccurrenceOrGroup),
    {
      id: "title",
      sortingFn: "alphanumeric",
    },
  ),
];

export const PlanningSideBar = () => {
  const { value: patientOrActivityTitleFilterValue } =
    usePatientOrActivityTitleFilter();
  const { t } = useLingui();
  const NO_GROUPING = t`Ingen gruppering`;
  const selectedDate = useSelectedDate();

  const { selectedItem } = useSelection();

  const isSidebarDropZoneAvailable = useIsSidebarDropZoneAvailable();

  const { planningMap } = useMap();

  const timeFilter = useTimeFilter();
  const { selected: selectedCompetences, allSelected: allCompetencesSelected } =
    useCompetenceFilter();
  const { selected: selectedTeams, allSelected: allTeamsSelected } =
    useTeamFilter();

  const {
    data: unplannedOccurrencesAndGroups,
    isPending,
    isError,
    error,
  } = useUnplannedOccurrencesAndGroups({
    fromDate: selectedDate,
    toDate: timeFilter?.endTime ?? selectedDate,
  });

  useLayoutEffect(() => {
    if (!planningMap) {
      // planningMap is probably closed, or just not loaded, so this is a no-op
      return;
    } else {
      planningMap.resize();
    }
  }, [unplannedOccurrencesAndGroups, planningMap]);

  const { dragAndDropHooks } = useDragAndDropFromSidebar({
    unplannedOccurrencesAndGroups,
  });

  const { handleDrop, isPending: isUnplanningPending } = useDropToSidebar();

  const { handleSelectionChange, selectedKeys } = useSelectionInSidebar({
    unplannedOccurrencesAndGroups,
  });

  const { userId } = useUserProfile();
  const [stringGrouping, setStringGrouping] = useLocalStorage({
    key: "planning.sidebarGrouping",
    userId,
    initialValue: "category",
  });
  const grouping = useMemo(() => [stringGrouping], [stringGrouping]);

  const sorting = useMemo(() => {
    return [
      ...grouping.map((group) => ({ id: group, desc: false })),
      ...DEFAULT_SORTING,
    ];
  }, [grouping]);

  const columnFilters = useMemo(() => {
    return [
      {
        id: "timespan",
        value: timeFilter,
      },
      // Patient or activity title search from PageToolbar is handled in the same filterFn, so only the patient column needs a filter.
      {
        id: "patient",
        value: patientOrActivityTitleFilterValue,
      },
      {
        id: "requiredCompetences",
        value: allCompetencesSelected ? undefined : selectedCompetences,
      },
      {
        id: "team",
        value: allTeamsSelected ? undefined : selectedTeams,
      },
    ];
  }, [
    timeFilter,
    patientOrActivityTitleFilterValue,
    allCompetencesSelected,
    selectedCompetences,
    allTeamsSelected,
    selectedTeams,
  ]);

  const { data: areaEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.Area,
  );

  const { data: teams } = useTeams();
  const { data: employees } = useEmployees();
  const { data: patientFields } = usePatientInformationFields();

  const employeesMap = useMemo(() => {
    const map = new Map<number, IEmployee>();
    if (employees) {
      employees.forEach((employee) => {
        map.set(employee.id, employee);
      });
    }
    return map;
  }, [employees]);

  const data = useMemo(
    () =>
      unplannedOccurrencesAndGroups
        ? getDataFromActivityOccurrencesAndGroups(unplannedOccurrencesAndGroups)
        : [],
    [unplannedOccurrencesAndGroups],
  );

  const translatedColumns = columns(t, employeesMap);

  // oxlint-disable-next-line react/incompatible-library -- TanStack Table's instance cannot be memoized by React Compiler.
  const table = useReactTable({
    data,
    columns: translatedColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    groupedColumnMode: false, // Don't move grouped columns to the left
    state: {
      grouping,
      sorting,
      columnFilters,
    },
  });

  if (isPending) {
    return <Loading message={t`Hämtar oplanerade aktiviteter`} padding={24} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta oplanerade aktiviteter.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  // See docs to understand the different getters: https://tanstack.com/table/v8/docs/guide/row-models#the-order-of-row-model-execution
  const initialRowCount = table.getCoreRowModel().rows.length;
  const filteredRowsCount =
    initialRowCount - table.getFilteredRowModel().rows.length;

  return (
    <LoadingOverlay show={isUnplanningPending}>
      <div className={styles.sidebarWrapper}>
        <div className={styles.planningSideBarHeader}>
          <Heading level="h2">
            <Trans>Att planera</Trans>
          </Heading>
          <div className={styles.countBadge}>{initialRowCount}</div>
          {filteredRowsCount >= 1 ? (
            <Text element="span" size="small">
              <Trans>{filteredRowsCount} döljs av filter</Trans>
            </Text>
          ) : null}
        </div>
        <div className={styles.columnSelect}>
          <ControlledSelect
            value={grouping[0] ?? NO_GROUPING}
            onChange={(value) => setStringGrouping(value)}
            label={t`Gruppera på:`}
            size="compact"
          >
            <option>{NO_GROUPING}</option>
            {GROUPABLE_COLUMNS.map((column) => {
              if (column === "areaDisplayName" && !areaEnabled) {
                return null;
              }
              if (column === "team" && !(teams && teams.length > 0)) {
                return null;
              }
              if (
                column === "primaryResponsible" &&
                patientFields?.PrimaryResponsible ===
                  informationFieldConfigValueSchema.enum.hidden
              ) {
                return null;
              }
              return (
                <option key={column} value={column}>
                  {t(COLUMN_DISPLAY_NAMES[column])}
                </option>
              );
            })}
          </ControlledSelect>
        </div>
        <DropZone
          ariaLabel={t`Att planera`}
          onDrop={handleDrop}
          className={clsx(
            styles.dropZone,
            isSidebarDropZoneAvailable && styles.activeDropZone,
          )}
          isDisabled={!isSidebarDropZoneAvailable || isUnplanningPending}
        >
          {table.getRowModel().rows.length === 0 ? (
            <NoResults
              message={
                filteredRowsCount >= 1
                  ? t`Inga aktiviteter att planera (${filteredRowsCount} döljs av filter)`
                  : t`Inga aktiviteter att planera`
              }
              padding={24}
            />
          ) : (
            <MedomaGridList
              aria-label={t`Oplanerade aktiviteter`}
              selectionMode="single"
              items={table.getRowModel().rows}
              onSelectionChange={handleSelectionChange}
              dragAndDropHooks={dragAndDropHooks}
              dependencies={[selectedItem, unplannedOccurrencesAndGroups]}
              className={styles.unplannedOccurrencesAndGroups}
            >
              {(row) => <GroupedRows row={row} selectedKeys={selectedKeys} />}
            </MedomaGridList>
          )}
        </DropZone>
      </div>
    </LoadingOverlay>
  );
};
